import { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Plus,
  Trash2,
  ShoppingBasket,
  Pencil,
  Check,
  X,
  CheckCircle2,
  Printer,
  CalendarDays,
} from "lucide-react";
import { Badge } from "../components/ui/badge";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "../components/ui/accordion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TERMS = ["Term 1", "Term 2", "Term 3"];
const UNITS = ["kg", "g", "litres", "ml", "packs", "bags", "bottles", "tins", "boxes", "pieces"];

function getCurrentTermByMonth(date = new Date()) {
  const month = date.getMonth() + 1;
  if (month >= 1 && month <= 3) return "Term 1";
  if (month >= 4 && month <= 8) return "Term 2";
  return "Term 3";
}

function escapeHtml(value) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/\"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

export default function GroceryPage() {
  const { user } = useAuth();

  const [studentStatsFilter, setStudentStatsFilter] = useState("all");
  const [items, setItems] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [allSubmissions, setAllSubmissions] = useState([]);
  const [students, setStudents] = useState([]);
  const [stats, setStats] = useState([]);
  const [loading, setLoading] = useState(true);

  const [selectedTerm, setSelectedTerm] = useState(getCurrentTermByMonth());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());
  const [selectedStudent, setSelectedStudent] = useState("");
  const [studentSelectSearch, setStudentSelectSearch] = useState("");
  const [schoolSettings, setSchoolSettings] = useState({});

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({ name: "", quantity: "1", unit: "kg" });
  const [savingItem, setSavingItem] = useState(null);

  const [dailyDate, setDailyDate] = useState(new Date().toISOString().split("T")[0]);
  const [dailyData, setDailyData] = useState({ submissions: [], summary: [] });
  const [dailyLoading, setDailyLoading] = useState(false);

  const [collapse, setCollapse] = useState({
    groceryList: false,
    collectionStats: false,
    groceryStock: false,
    studentStats: false,
    dailySummary: false,
    dailyDetail: false,
  });

  const toggleCollapse = (key) =>
    setCollapse((prev) => ({ ...prev, [key]: !prev[key] }));

  const fetchItems = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/groceries/items`, {
        params: { term: selectedTerm, year: selectedYear },
      });
      setItems(Array.isArray(res.data) ? res.data : []);
    } catch {
      toast.error("Failed to fetch grocery items");
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [selectedTerm, selectedYear]);

  const fetchStudents = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/students`);
      setStudents(Array.isArray(res.data) ? res.data : []);
    } catch {
      setStudents([]);
    }
  }, []);

  const fetchSubmissions = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/groceries/submissions`, {
        params: { term: selectedTerm, year: selectedYear },
      });
      setSubmissions(Array.isArray(res.data) ? res.data : []);
    } catch {
      toast.error("Failed to fetch submissions");
      setSubmissions([]);
    }
  }, [selectedTerm, selectedYear]);

  const fetchAllSubmissions = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/groceries/submissions`);
      setAllSubmissions(Array.isArray(res.data) ? res.data : []);
    } catch {
      setAllSubmissions([]);
    }
  }, []);

  const fetchStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/groceries/stats`, {
        params: { term: selectedTerm, year: selectedYear },
      });
      setStats(Array.isArray(res.data) ? res.data : []);
    } catch {
      toast.error("Failed to fetch stats");
      setStats([]);
    }
  }, [selectedTerm, selectedYear]);

  const fetchSchoolSettings = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/settings`);
      setSchoolSettings(res.data && typeof res.data === "object" ? res.data : {});
    } catch {
      setSchoolSettings({});
    }
  }, []);

  const fetchDailyStats = useCallback(async () => {
    try {
      setDailyLoading(true);
      const res = await axios.get(`${API}/groceries/daily-stats`, {
        params: { date: dailyDate },
      });
      setDailyData(
        res.data && typeof res.data === "object"
          ? res.data
          : { submissions: [], summary: [] }
      );
    } catch {
      toast.error("Failed to fetch daily stats");
      setDailyData({ submissions: [], summary: [] });
    } finally {
      setDailyLoading(false);
    }
  }, [dailyDate]);

  useEffect(() => {
    fetchItems();

    if (user?.role === "teacher" || user?.role === "admin" || user?.role === "student") {
      fetchStudents();
      fetchSubmissions();
      fetchAllSubmissions();
    }

    if (user?.role === "admin") {
      fetchStats();
      fetchSchoolSettings();
    }
  }, [
    fetchAllSubmissions,
    fetchItems,
    fetchSchoolSettings,
    fetchStats,
    fetchStudents,
    fetchSubmissions,
    user?.role,
  ]);

  useEffect(() => {
    if (user?.role === "admin" && dailyDate) {
      fetchDailyStats();
    }
  }, [dailyDate, fetchDailyStats, user?.role]);

  const handleSaveItem = async (e) => {
    e.preventDefault();
    try {
      if (editingItem) {
        await axios.put(`${API}/groceries/items/${editingItem.id}`, {
          ...formData,
          term: selectedTerm,
          academic_year: selectedYear,
        });
        toast.success("Item updated");
      } else {
        await axios.post(`${API}/groceries/items`, {
          ...formData,
          term: selectedTerm,
          academic_year: selectedYear,
        });
        toast.success("Item added");
      }

      setIsModalOpen(false);
      setEditingItem(null);
      setFormData({ name: "", quantity: "1", unit: "kg" });

      fetchItems();
      fetchSubmissions();
      fetchAllSubmissions();
      if (user?.role === "admin") fetchStats();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to save item");
    }
  };

  const handleDeleteItem = async (id) => {
    if (!window.confirm("Delete this grocery item?")) return;
    try {
      await axios.delete(`${API}/groceries/items/${id}`);
      toast.success("Item deleted");
      fetchItems();
      fetchSubmissions();
      fetchAllSubmissions();
      if (user?.role === "admin") fetchStats();
    } catch {
      toast.error("Failed to delete item");
    }
  };

  const openEditModal = (item) => {
    setEditingItem(item);
    setFormData({
      name: item.name,
      quantity: item.quantity?.toString() || "1",
      unit: item.unit,
    });
    setIsModalOpen(true);
  };

  const handleToggle = async (grocery_item_id) => {
    if (!selectedStudent) return;
    setSavingItem(grocery_item_id);

    try {
      await axios.post(`${API}/groceries/submissions`, {
        student_id: selectedStudent,
        grocery_item_id,
      });

      await fetchSubmissions();
      await fetchAllSubmissions();
      if (user?.role === "admin") await fetchStats();
    } catch {
      toast.error("Failed to update");
    } finally {
      setSavingItem(null);
    }
  };

  const isItemBroughtForStudent = (studentId, grocery_item_id, source = submissions) => {
    return source.some(
      (s) =>
        String(s.student_id) === String(studentId) &&
        String(s.grocery_item_id) === String(grocery_item_id)
    );
  };

  const isItemBrought = (grocery_item_id) => {
    return isItemBroughtForStudent(selectedStudent, grocery_item_id, submissions);
  };

  const getStudentGroceryStatus = (studentId) => {
    const broughtIds = submissions
      .filter((s) => String(s.student_id) === String(studentId))
      .map((s) => String(s.grocery_item_id));

    return items.map((item) => ({
      ...item,
      brought: broughtIds.includes(String(item.id)),
    }));
  };

  const getStudentStatusLabel = (studentId) => {
    const status = getStudentGroceryStatus(studentId);
    const broughtCount = status.filter((i) => i.brought).length;
    if (broughtCount === 0) return "None";
    if (broughtCount === items.length && items.length > 0) return "Complete";
    return "Partial";
  };

  const filterStudentsByStatus = (studentList) => {
    if (studentStatsFilter === "all") return studentList;
    return studentList.filter((s) => {
      const status = getStudentGroceryStatus(s.id);
      const broughtCount = status.filter((i) => i.brought).length;
      if (studentStatsFilter === "complete") {
        return broughtCount === items.length && items.length > 0;
      }
      if (studentStatsFilter === "partial") {
        return broughtCount > 0 && broughtCount < items.length;
      }
      if (studentStatsFilter === "none") {
        return broughtCount === 0;
      }
      return true;
    });
  };

  const studentsByClass = useMemo(() => {
    return students.reduce((acc, s) => {
      if (!acc[s.class_name]) acc[s.class_name] = [];
      acc[s.class_name].push(s);
      return acc;
    }, {});
  }, [students]);

  const selectedStudentObj = students.find(
    (s) => String(s.id) === String(selectedStudent)
  );

  const broughtCount = items.filter((item) => isItemBrought(item.id)).length;

  const groupedStatsByClassName = useMemo(() => {
    return stats.reduce((acc, row) => {
      if (!acc[row.class_name]) acc[row.class_name] = [];
      acc[row.class_name].push(row);
      return acc;
    }, {});
  }, [stats]);

  const loggedInStudent = useMemo(() => {
    if (user?.role !== "student") return null;

    return (
      students.find((s) => {
        if (user?.student_id && String(s.id) === String(user.student_id)) return true;
        if (user?.id && String(s.id) === String(user.id)) return true;
        if (user?.student_id_code && s.student_id_code === user.student_id_code) return true;
        if (user?.email && s.email && s.email.toLowerCase() === user.email.toLowerCase()) return true;
        return false;
      }) || null
    );
  }, [students, user]);

  const filteredTeacherStudents = students.filter((s) => {
    const q = studentSelectSearch.trim().toLowerCase();
    return (
      !q ||
      s.first_name?.toLowerCase().includes(q) ||
      s.last_name?.toLowerCase().includes(q) ||
      s.student_id_code?.toLowerCase().includes(q)
    );
  });

  const handlePrintClassStudentStats = (className, filter = "all") => {
    const classStudents = students.filter((s) => s.class_name === className);
    const filtered = (() => {
      if (filter === "all") return classStudents;
      return classStudents.filter((stu) => {
        const status = getStudentStatusLabel(stu.id);
        if (filter === "complete") return status === "Complete";
        if (filter === "partial") return status === "Partial";
        if (filter === "none") return status === "None";
        return true;
      });
    })();

    const classItems = groupedStatsByClassName[className] || [];

    if (filtered.length === 0) {
      toast.error(`No ${filter} students found for ${className}`);
      return;
    }

    const headerColumns = classItems
      .map(
        (item) =>
          `<th style="border:1px solid #000;padding:6px;text-align:center;">${item.item_name}</th>`
      )
      .join("");

    const rows = filtered
      .map((stu, idx) => {
        const status = getStudentGroceryStatus(stu.id);
        const itemCells = status
          .map(
            (item) =>
              `<td style="border:1px solid #000;padding:6px;text-align:center;font-weight:bold;color:${
                item.brought ? "green" : "red"
              };">${item.brought ? "✓" : "✗"}</td>`
          )
          .join("");

        return `
          <tr>
            <td style="border:1px solid #000;padding:6px;text-align:center;">${idx + 1}</td>
            <td style="border:1px solid #000;padding:6px;">${stu.first_name} ${stu.last_name}</td>
            <td style="border:1px solid #000;padding:6px;text-align:center;">${getStudentStatusLabel(
              stu.id
            )}</td>
            ${itemCells}
          </tr>
        `;
      })
      .join("");

    const html = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>${className} Student Grocery Stats - ${filter}</title>
        <style>
          body{font-family:Arial,sans-serif;font-size:12px;}
          table{border-collapse:collapse;width:100%;margin-bottom:20px;}
          @media print{body{-webkit-print-color-adjust:exact;}}
        </style>
      </head>
      <body>
        <div style="text-align:center;margin-bottom:16px;">
          <h2 style="margin:0;">CRESABEL SCHOOL</h2>
          <h3 style="margin:4px 0;">Student Grocery Stats</h3>
          <p style="margin:2px 0;">${className} | ${filter.toUpperCase()} | ${selectedTerm} ${selectedYear}</p>
        </div>
        <table>
          <thead>
            <tr style="background:#f0f0f0;">
              <th style="border:1px solid #000;padding:6px;">#</th>
              <th style="border:1px solid #000;padding:6px;">Student</th>
              <th style="border:1px solid #000;padding:6px;">Status</th>
              ${headerColumns}
            </tr>
          </thead>
          <tbody>${rows}</tbody>
        </table>
      </body>
      </html>
    `;

    const w = window.open("", "_blank");
    if (!w) return;
    w.document.write(html);
    w.document.close();
    w.focus();
    setTimeout(() => w.print(), 400);
  };

  const handlePrintAllGroceries = () => {
    const stockMap = {};
    stats.forEach((row) => {
      if (!stockMap[row.item_id]) {
        stockMap[row.item_id] = {
          name: row.item_name,
          quantity: Number(row.quantity),
          unit: row.unit,
          totalStudents: 0,
          studentsBrought: 0,
        };
      }
      stockMap[row.item_id].totalStudents += Number(row.total_students);
      stockMap[row.item_id].studentsBrought += Number(row.students_brought);
    });

    const rows = Object.values(stockMap)
      .map((item, idx) => {
        const collected = item.studentsBrought * item.quantity;
        const expected = item.totalStudents * item.quantity;
        const outstanding = expected - collected;
        return `
          <tr>
            <td style="border:1px solid #000;padding:6px;text-align:center;">${idx + 1}</td>
            <td style="border:1px solid #000;padding:6px;">${item.name}</td>
            <td style="border:1px solid #000;padding:6px;text-align:center;">${item.quantity} ${item.unit}</td>
            <td style="border:1px solid #000;padding:6px;text-align:center;">${item.studentsBrought} / ${item.totalStudents}</td>
            <td style="border:1px solid #000;padding:6px;text-align:center;">${collected} ${item.unit}</td>
            <td style="border:1px solid #000;padding:6px;text-align:center;">${
              outstanding > 0 ? `${outstanding} ${item.unit}` : "Complete"
            }</td>
          </tr>
        `;
      })
      .join("");

    const html = `<!DOCTYPE html><html><head><title>All Groceries Stock Report</title><style>body{font-family:Arial,sans-serif;font-size:12px;}table{border-collapse:collapse;width:100%;margin-bottom:20px;}@media print{body{-webkit-print-color-adjust:exact;}}</style></head><body><div style="text-align:center;margin-bottom:16px;"><h2 style="margin:0;">CRESABEL SCHOOL</h2><h3 style="margin:4px 0;">All Groceries Stock Report</h3></div><table><thead><tr style="background:#f0f0f0;"><th style="border:1px solid #000;padding:6px;">#</th><th style="border:1px solid #000;padding:6px;">Item</th><th style="border:1px solid #000;padding:6px;">Per Child</th><th style="border:1px solid #000;padding:6px;">Students Brought</th><th style="border:1px solid #000;padding:6px;">Total Collected</th><th style="border:1px solid #000;padding:6px;">Outstanding</th></tr></thead><tbody>${rows}</tbody></table></body></html>`;

    const w = window.open("", "_blank");
    if (!w) return;
    w.document.write(html);
    w.document.close();
    w.focus();
    setTimeout(() => w.print(), 400);
  };

  const handlePrintGroceryList = () => {
    if (items.length === 0) {
      toast.error("No grocery items to print");
      return;
    }

    const generatedOn = new Date().toLocaleDateString("en-GB", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });

    const getSetting = (...keys) => {
      for (const key of keys) {
        const value = schoolSettings?.[key];
        if (typeof value === "string" && value.trim()) return value.trim();
      }
      return "";
    };

    const schoolName = getSetting("school_name", "schoolName") || "CRESABEL SCHOOL";
    const schoolMotto =
      getSetting("school_motto", "school_tagline", "school_subtitle") ||
      "Academic Excellence | Discipline | Character";
    const schoolPhone = getSetting("school_phone", "phone_number", "phone");
    const schoolAddress = getSetting("school_address", "physical_address", "address");

    const contactLine = [schoolPhone ? `Tel: ${schoolPhone}` : "", schoolAddress]
      .filter(Boolean)
      .join(" | ");

    const rows = items
      .map(
        (item, idx) => `
          <tr>
            <td>${idx + 1}</td>
            <td>${item.name}</td>
            <td>${item.quantity}</td>
            <td>${item.unit}</td>
          </tr>
        `
      )
      .join("");

    const html = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Grocery List - ${selectedTerm} ${selectedYear}</title>
        <style>
          @page { size: A4; margin: 14mm 12mm 14mm 12mm; }
          * { box-sizing: border-box; }
          body {
            margin: 0;
            color: #0f172a;
            font-family: "Segoe UI", Arial, sans-serif;
            font-size: 12px;
            line-height: 1.4;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          .sheet {
            width: 100%;
          }
          .letterhead {
            border: 2px solid #0f172a;
            border-left: 0;
            border-right: 0;
            padding: 10px 0 8px;
            text-align: center;
            margin-bottom: 14px;
          }
          .school-name {
            margin: 0;
            font-size: 22px;
            letter-spacing: 1px;
            font-weight: 800;
          }
          .school-sub {
            margin: 2px 0 0;
            font-size: 11px;
            color: #334155;
          }
          .report-title {
            margin: 0;
            font-size: 16px;
            font-weight: 700;
            text-align: center;
          }
          .report-meta {
            margin: 6px 0 12px;
            text-align: center;
            color: #334155;
            font-size: 11.5px;
          }
          table {
            width: 100%;
            border-collapse: collapse;
            margin-top: 6px;
          }
          th, td {
            border: 1px solid #0f172a;
            padding: 8px;
            text-align: left;
            vertical-align: middle;
          }
          th {
            background: #e2e8f0;
            font-weight: 700;
          }
          td:nth-child(1),
          td:nth-child(3),
          td:nth-child(4) {
            text-align: center;
          }
          .footer-note {
            margin-top: 12px;
            font-size: 11px;
            color: #475569;
          }
        </style>
      </head>
      <body>
        <div class="sheet">
          <div class="letterhead">
            <h1 class="school-name">${escapeHtml(schoolName)}</h1>
            <p class="school-sub">${escapeHtml(schoolMotto)}</p>
            ${contactLine ? `<p class="school-sub">${escapeHtml(contactLine)}</p>` : ""}
          </div>

          <h2 class="report-title">Grocery List</h2>
          <p class="report-meta">${selectedTerm} ${selectedYear} | ${items.length} items</p>

          <table>
            <thead>
              <tr>
                <th style="width: 52px;">#</th>
                <th>Item Name</th>
                <th style="width: 110px;">Quantity</th>
                <th style="width: 100px;">Unit</th>
              </tr>
            </thead>
            <tbody>${rows}</tbody>
          </table>

          <p class="footer-note">Generated on ${generatedOn}</p>
        </div>
      </body>
      </html>
    `;

    const w = window.open("", "_blank");
    if (!w) return;
    w.document.write(html);
    w.document.close();
    w.focus();
    setTimeout(() => w.print(), 400);
  };

  const handlePrintDailySummary = () => {
    const { summary } = dailyData;
    const displayDate = new Date(dailyDate + "T00:00:00").toLocaleDateString("en-GB", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });

    if (!summary.length) {
      toast.error("No summary data to print");
      return;
    }

    const summaryRows = summary
      .map(
        (r, i) =>
          `<tr><td style="border:1px solid #000;padding:6px;text-align:center;">${i + 1}</td><td style="border:1px solid #000;padding:6px;">${r.item_name}</td><td style="border:1px solid #000;padding:6px;text-align:center;">${r.students_count}</td><td style="border:1px solid #000;padding:6px;text-align:center;">${Number(r.total_collected).toFixed(1)} ${r.unit}</td></tr>`
      )
      .join("");

    const html = `<!DOCTYPE html><html><head><title>Daily Grocery Summary - ${displayDate}</title><style>body{font-family:Arial,sans-serif;font-size:12px;}table{border-collapse:collapse;width:100%;margin-bottom:20px;}@media print{body{-webkit-print-color-adjust:exact;}}</style></head><body><div style="text-align:center;margin-bottom:16px;"><h2 style="margin:0;">CRESABEL SCHOOL</h2><h3 style="margin:4px 0;">Daily Grocery Summary</h3><p style="margin:2px 0;">${displayDate}</p></div><h4 style="margin:12px 0 6px;">Summary (${summary.length} items)</h4><table><thead><tr style="background:#f0f0f0;"><th style="border:1px solid #000;padding:6px;">#</th><th style="border:1px solid #000;padding:6px;">Item</th><th style="border:1px solid #000;padding:6px;">Students</th><th style="border:1px solid #000;padding:6px;">Total Collected</th></tr></thead><tbody>${summaryRows}</tbody></table></body></html>`;

    const w = window.open("", "_blank");
    if (!w) return;
    w.document.write(html);
    w.document.close();
    w.focus();
    setTimeout(() => w.print(), 400);
  };

  const handlePrintDailyDetail = () => {
    const { submissions } = dailyData;
    const displayDate = new Date(dailyDate + "T00:00:00").toLocaleDateString("en-GB", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });

    if (!submissions.length) {
      toast.error("No detail data to print");
      return;
    }

    const detailRows = submissions
      .map(
        (r, i) =>
          `<tr><td style="border:1px solid #000;padding:6px;text-align:center;">${i + 1}</td><td style="border:1px solid #000;padding:6px;">${r.last_name} ${r.first_name}</td><td style="border:1px solid #000;padding:6px;">${r.class_name}</td><td style="border:1px solid #000;padding:6px;">${r.item_name} (${r.quantity} ${r.unit})</td><td style="border:1px solid #000;padding:6px;text-align:center;">${new Date(
            r.created_at
          ).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}</td></tr>`
      )
      .join("");

    const html = `<!DOCTYPE html><html><head><title>Daily Grocery Detail - ${displayDate}</title><style>body{font-family:Arial,sans-serif;font-size:12px;}table{border-collapse:collapse;width:100%;margin-bottom:20px;}@media print{body{-webkit-print-color-adjust:exact;}}</style></head><body><div style="text-align:center;margin-bottom:16px;"><h2 style="margin:0;">CRESABEL SCHOOL</h2><h3 style="margin:4px 0;">Daily Grocery Detail</h3><p style="margin:2px 0;">${displayDate}</p></div><h4 style="margin:12px 0 6px;">Detail (${submissions.length} entries)</h4><table><thead><tr style="background:#f0f0f0;"><th style="border:1px solid #000;padding:6px;">#</th><th style="border:1px solid #000;padding:6px;">Student</th><th style="border:1px solid #000;padding:6px;">Class</th><th style="border:1px solid #000;padding:6px;">Item</th><th style="border:1px solid #000;padding:6px;">Time</th></tr></thead><tbody>${detailRows}</tbody></table></body></html>`;

    const w = window.open("", "_blank");
    if (!w) return;
    w.document.write(html);
    w.document.close();
    w.focus();
    setTimeout(() => w.print(), 400);
  };

  if (user?.role === "admin") {
    const classMap = {};
    stats.forEach((row) => {
      if (!classMap[row.class_id]) {
        classMap[row.class_id] = { class_name: row.class_name, items: [] };
      }
      classMap[row.class_id].items.push(row);
    });

    return (
      <div>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="font-heading font-bold text-3xl text-slate-900 flex items-center gap-3">
              <ShoppingBasket className="text-pink-500" size={32} />
              Grocery Management
            </h1>
            <p className="text-slate-500 mt-1">Manage term grocery requirements and track collection</p>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={() => {
                setEditingItem(null);
                setFormData({ name: "", quantity: "1", unit: "kg" });
                setIsModalOpen(true);
              }}
              className="bg-pink-500 hover:bg-pink-600 text-white"
            >
              <Plus size={18} className="mr-2" /> Add Grocery Item
            </Button>
          </div>
        </div>

        <div className="flex flex-wrap gap-4 mb-6">
          <div className="w-40">
            <Label className="text-sm mb-1 block">Term</Label>
            <Select value={selectedTerm} onValueChange={setSelectedTerm}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {TERMS.map((t) => (
                  <SelectItem key={t} value={t}>
                    {t}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="w-28">
            <Label className="text-sm mb-1 block">Year</Label>
            <Input type="number" value={selectedYear} onChange={(e) => setSelectedYear(e.target.value)} />
          </div>
        </div>

        <Card className="border-slate-200 mb-6">
          <CardHeader
            className="pb-3 flex items-center justify-between cursor-pointer"
            onClick={() => toggleCollapse("groceryList")}
          >
            <CardTitle className="font-heading text-lg">
              Grocery List — {selectedTerm} {selectedYear} ({items.length} items)
            </CardTitle>
            <div className="flex items-center gap-2">
              {items.length > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    handlePrintGroceryList();
                  }}
                >
                  <Printer size={14} className="mr-1" />
                  Print List
                </Button>
              )}
              <span>{collapse.groceryList ? "▼" : "►"}</span>
            </div>
          </CardHeader>
          {collapse.groceryList && (
            <CardContent>
              {loading ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pink-500"></div>
                </div>
              ) : items.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <ShoppingBasket size={48} className="mx-auto mb-4 opacity-50" />
                  <p>No grocery items for this term. Click "Add Grocery Item" to start.</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50 hover:bg-slate-50">
                      <TableHead className="font-medium text-slate-500">#</TableHead>
                      <TableHead className="font-medium text-slate-500">Item Name</TableHead>
                      <TableHead className="font-medium text-slate-500">Quantity</TableHead>
                      <TableHead className="font-medium text-slate-500">Unit</TableHead>
                      <TableHead className="font-medium text-slate-500 text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {items.map((item, idx) => (
                      <TableRow key={item.id} className="table-row-hover">
                        <TableCell className="text-slate-500">{idx + 1}</TableCell>
                        <TableCell className="font-medium text-slate-900">{item.name}</TableCell>
                        <TableCell className="text-slate-600">{item.quantity}</TableCell>
                        <TableCell className="text-slate-600">{item.unit}</TableCell>
                        <TableCell className="text-right space-x-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEditModal(item)}
                            className="text-slate-500 hover:text-blue-500"
                          >
                            <Pencil size={16} />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteItem(item.id)}
                            className="text-slate-500 hover:text-red-500"
                          >
                            <Trash2 size={16} />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          )}
        </Card>

        {Object.keys(classMap).length > 0 && (
          <Card className="border-slate-200">
            <CardHeader
              className="pb-3 flex items-center justify-between cursor-pointer"
              onClick={() => toggleCollapse("collectionStats")}
            >
              <CardTitle className="font-heading text-lg">Collection Stats by Class</CardTitle>
              <span>{collapse.collectionStats ? "▼" : "►"}</span>
            </CardHeader>
            {collapse.collectionStats && (
              <CardContent>
                <div className="space-y-6">
                  {Object.values(classMap).map((cls) => (
                    <div key={cls.class_name}>
                      <h3 className="font-heading font-semibold text-slate-900 mb-2">{cls.class_name}</h3>
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-slate-50 hover:bg-slate-50">
                            <TableHead className="font-medium text-slate-500">Item</TableHead>
                            <TableHead className="font-medium text-slate-500">Required (per child)</TableHead>
                            <TableHead className="font-medium text-slate-500">Students Brought</TableHead>
                            <TableHead className="font-medium text-slate-500">Total Students</TableHead>
                            <TableHead className="font-medium text-slate-500">Progress</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {cls.items.map((row) => {
                            const pct =
                              row.total_students > 0
                                ? Math.round((row.students_brought / row.total_students) * 100)
                                : 0;
                            return (
                              <TableRow key={row.item_id}>
                                <TableCell className="font-medium text-slate-900">{row.item_name}</TableCell>
                                <TableCell className="text-slate-600">
                                  {row.quantity} {row.unit}
                                </TableCell>
                                <TableCell className="font-bold text-slate-900">{row.students_brought}</TableCell>
                                <TableCell className="text-slate-600">{row.total_students}</TableCell>
                                <TableCell>
                                  <div className="flex items-center gap-2">
                                    <div className="w-24 h-2 rounded-full bg-slate-200 overflow-hidden">
                                      <div
                                        className={`h-full rounded-full ${
                                          pct === 100 ? "bg-green-500" : pct > 50 ? "bg-yellow-500" : "bg-pink-500"
                                        }`}
                                        style={{ width: `${pct}%` }}
                                      />
                                    </div>
                                    <span className="text-sm font-medium text-slate-600">{pct}%</span>
                                  </div>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </div>
                  ))}
                </div>
              </CardContent>
            )}
          </Card>
        )}

        {items.length > 0 && (
          <Card className="border-slate-200 mt-6">
            <CardHeader
              className="pb-3 flex items-center justify-between cursor-pointer"
              onClick={() => toggleCollapse("groceryStock")}
            >
              <CardTitle className="font-heading text-lg">Grocery Stock</CardTitle>
              <span>{collapse.groceryStock ? "▼" : "►"}</span>
            </CardHeader>
            {collapse.groceryStock && (
              <CardContent>
                <div className="flex justify-end mb-4">
                  <Button variant="outline" size="sm" onClick={handlePrintAllGroceries}>
                    <Printer size={14} className="mr-1" />
                    Print Stock
                  </Button>
                </div>

                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50 hover:bg-slate-50">
                      <TableHead className="font-medium text-slate-500">#</TableHead>
                      <TableHead className="font-medium text-slate-500">Item</TableHead>
                      <TableHead className="font-medium text-slate-500">Per Child</TableHead>
                      <TableHead className="font-medium text-slate-500">Students Brought</TableHead>
                      <TableHead className="font-medium text-slate-500">Total Collected</TableHead>
                      <TableHead className="font-medium text-slate-500">Outstanding</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(() => {
                      const stockMap = {};
                      stats.forEach((row) => {
                        if (!stockMap[row.item_id]) {
                          stockMap[row.item_id] = {
                            name: row.item_name,
                            quantity: Number(row.quantity),
                            unit: row.unit,
                            totalStudents: 0,
                            studentsBrought: 0,
                          };
                        }
                        stockMap[row.item_id].totalStudents += Number(row.total_students);
                        stockMap[row.item_id].studentsBrought += Number(row.students_brought);
                      });

                      return Object.values(stockMap).map((item, idx) => {
                        const collected = item.studentsBrought * item.quantity;
                        const expected = item.totalStudents * item.quantity;
                        const outstanding = expected - collected;
                        return (
                          <TableRow key={idx}>
                            <TableCell className="text-slate-500">{idx + 1}</TableCell>
                            <TableCell className="font-medium text-slate-900">{item.name}</TableCell>
                            <TableCell className="text-slate-600">
                              {item.quantity} {item.unit}
                            </TableCell>
                            <TableCell className="text-slate-600">
                              {item.studentsBrought} / {item.totalStudents}
                            </TableCell>
                            <TableCell className="font-bold text-green-700">
                              {collected} {item.unit}
                            </TableCell>
                            <TableCell>
                              <span
                                className={
                                  outstanding > 0 ? "font-bold text-red-600" : "font-bold text-green-600"
                                }
                              >
                                {outstanding > 0 ? `${outstanding} ${item.unit}` : "Complete"}
                              </span>
                            </TableCell>
                          </TableRow>
                        );
                      });
                    })()}
                  </TableBody>
                </Table>
              </CardContent>
            )}
          </Card>
        )}

        <Card className="border-slate-200 mt-6">
          <CardHeader
            className="pb-3 flex items-center justify-between cursor-pointer"
            onClick={() => toggleCollapse("studentStats")}
          >
            <CardTitle className="font-heading text-lg">Student Grocery Stats by Class</CardTitle>
            <span>{collapse.studentStats ? "▼" : "►"}</span>
          </CardHeader>

          {collapse.studentStats && (
            <CardContent>
              <div className="flex flex-wrap gap-2 mb-4">
                <Button
                  size="sm"
                  variant={studentStatsFilter === "all" ? "default" : "outline"}
                  className={studentStatsFilter === "all" ? "bg-blue-500 hover:bg-blue-600" : ""}
                  onClick={() => setStudentStatsFilter("all")}
                >
                  All
                </Button>
                <Button
                  size="sm"
                  variant={studentStatsFilter === "partial" ? "default" : "outline"}
                  className={studentStatsFilter === "partial" ? "bg-amber-500 hover:bg-amber-600" : ""}
                  onClick={() => setStudentStatsFilter("partial")}
                >
                  Partial
                </Button>
                <Button
                  size="sm"
                  variant={studentStatsFilter === "complete" ? "default" : "outline"}
                  className={studentStatsFilter === "complete" ? "bg-green-500 hover:bg-green-600" : ""}
                  onClick={() => setStudentStatsFilter("complete")}
                >
                  Complete
                </Button>
                <Button
                  size="sm"
                  variant={studentStatsFilter === "none" ? "default" : "outline"}
                  onClick={() => setStudentStatsFilter("none")}
                >
                  None
                </Button>
              </div>

              {Object.keys(studentsByClass).length === 0 ? (
                <div className="text-center text-slate-500 py-8">No students found.</div>
              ) : (
                <Accordion type="multiple" className="w-full">
                  {Object.entries(studentsByClass).map(([className, classStudents]) => {
                    const filtered = filterStudentsByStatus(classStudents);
                    if (filtered.length === 0) return null;

                    return (
                      <AccordionItem key={className} value={className}>
                        <AccordionTrigger>{className}</AccordionTrigger>
                        <AccordionContent>
                          <div className="flex flex-wrap gap-2 mb-4">
                            <Button variant="outline" size="sm" onClick={() => handlePrintClassStudentStats(className, "all")}>
                              <Printer size={14} className="mr-1" />
                              Print All
                            </Button>
                            <Button variant="outline" size="sm" onClick={() => handlePrintClassStudentStats(className, "complete")}>
                              <Printer size={14} className="mr-1" />
                              Print Complete
                            </Button>
                            <Button variant="outline" size="sm" onClick={() => handlePrintClassStudentStats(className, "partial")}>
                              <Printer size={14} className="mr-1" />
                              Print Partial
                            </Button>
                            <Button variant="outline" size="sm" onClick={() => handlePrintClassStudentStats(className, "none")}>
                              <Printer size={14} className="mr-1" />
                              Print None
                            </Button>
                          </div>

                          <Table>
                            <TableHeader>
                              <TableRow className="bg-slate-50 hover:bg-slate-50">
                                <TableHead className="font-medium text-slate-500">Student</TableHead>
                                <TableHead className="font-medium text-slate-500 text-center">
                                  Overall Status
                                </TableHead>
                                {items.map((item) => (
                                  <TableHead key={item.id} className="font-medium text-slate-500 text-center">
                                    {item.name}
                                  </TableHead>
                                ))}
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {filtered.map((student) => {
                                const status = getStudentGroceryStatus(student.id);
                                return (
                                  <TableRow key={student.id}>
                                    <TableCell className="font-medium text-slate-900">
                                      {student.first_name} {student.last_name}
                                    </TableCell>
                                    <TableCell className="text-center font-medium">
                                      {getStudentStatusLabel(student.id)}
                                    </TableCell>
                                    {status.map((item) => (
                                      <TableCell key={item.id} className="text-center">
                                        {item.brought ? (
                                          <Check className="text-green-500 mx-auto" size={18} />
                                        ) : (
                                          <X className="text-red-500 mx-auto" size={18} />
                                        )}
                                      </TableCell>
                                    ))}
                                  </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                        </AccordionContent>
                      </AccordionItem>
                    );
                  })}
                </Accordion>
              )}
            </CardContent>
          )}
        </Card>

        <Card className="border-slate-200 mt-6">
          <CardHeader className="pb-3">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <CalendarDays size={20} className="text-pink-500" />
                Daily Collections
              </CardTitle>
              <div className="flex items-center gap-3">
                <Input
                  type="date"
                  value={dailyDate}
                  onChange={(e) => setDailyDate(e.target.value)}
                  className="w-44"
                />
                {dailyData.summary.length > 0 && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handlePrintDailySummary}
                    className="text-slate-500 hover:text-pink-600 hover:border-pink-300"
                  >
                    <Printer size={14} className="mr-1" />
                    Print Summary
                  </Button>
                )}
                {dailyData.submissions.length > 0 && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handlePrintDailyDetail}
                    className="text-slate-500 hover:text-pink-600 hover:border-pink-300"
                  >
                    <Printer size={14} className="mr-1" />
                    Print Detail
                  </Button>
                )}
              </div>
            </div>
          </CardHeader>

          <CardContent>
            {dailyLoading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pink-500"></div>
              </div>
            ) : dailyData.submissions.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <CalendarDays size={40} className="mx-auto mb-3 opacity-50" />
                <p>No grocery collections recorded on this date</p>
              </div>
            ) : (
              <div className="space-y-6">
                <Card className="mb-4">
                  <CardHeader
                    className="flex items-center justify-between cursor-pointer py-2 px-4"
                    onClick={() => toggleCollapse("dailySummary")}
                  >
                    <CardTitle className="font-heading text-base flex items-center gap-2">
                      Summary — {dailyData.summary.length} items, {dailyData.submissions.length} entries
                    </CardTitle>
                    <span>{collapse.dailySummary ? "▼" : "►"}</span>
                  </CardHeader>
                  {collapse.dailySummary && (
                    <CardContent className="pt-0">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-slate-50 hover:bg-slate-50">
                            <TableHead className="font-medium text-slate-500">#</TableHead>
                            <TableHead className="font-medium text-slate-500">Item</TableHead>
                            <TableHead className="font-medium text-slate-500">Students</TableHead>
                            <TableHead className="font-medium text-slate-500">Total Collected</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {dailyData.summary.map((row, idx) => (
                            <TableRow key={idx}>
                              <TableCell className="text-slate-500">{idx + 1}</TableCell>
                              <TableCell className="font-medium text-slate-900">{row.item_name}</TableCell>
                              <TableCell className="text-slate-600">{row.students_count}</TableCell>
                              <TableCell className="font-bold text-green-700">
                                {Number(row.total_collected).toFixed(1)} {row.unit}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </CardContent>
                  )}
                </Card>

                <Card>
                  <CardHeader
                    className="flex items-center justify-between cursor-pointer py-2 px-4"
                    onClick={() => toggleCollapse("dailyDetail")}
                  >
                    <CardTitle className="font-heading text-base flex items-center gap-2">
                      Detail
                    </CardTitle>
                    <span>{collapse.dailyDetail ? "▼" : "►"}</span>
                  </CardHeader>
                  {collapse.dailyDetail && (
                    <CardContent className="pt-0">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-slate-50 hover:bg-slate-50">
                            <TableHead className="font-medium text-slate-500">#</TableHead>
                            <TableHead className="font-medium text-slate-500">Student</TableHead>
                            <TableHead className="font-medium text-slate-500">Class</TableHead>
                            <TableHead className="font-medium text-slate-500">Item</TableHead>
                            <TableHead className="font-medium text-slate-500">Time</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {dailyData.submissions.map((row, idx) => (
                            <TableRow key={row.id ?? idx}>
                              <TableCell className="text-slate-500">{idx + 1}</TableCell>
                              <TableCell className="font-medium text-slate-900">
                                {row.last_name} {row.first_name}
                              </TableCell>
                              <TableCell className="text-slate-600">{row.class_name}</TableCell>
                              <TableCell className="text-slate-600">
                                {row.item_name} ({row.quantity} {row.unit})
                              </TableCell>
                              <TableCell className="text-slate-500 text-sm">
                                {new Date(row.created_at).toLocaleTimeString("en-GB", {
                                  hour: "2-digit",
                                  minute: "2-digit",
                                })}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </CardContent>
                  )}
                </Card>
              </div>
            )}
          </CardContent>
        </Card>

        <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
          <DialogContent className="sm:max-w-[420px]">
            <DialogHeader>
              <DialogTitle className="font-heading">
                {editingItem ? "Edit Grocery Item" : "Add Grocery Item"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSaveItem} className="space-y-4 mt-2">
              <div className="space-y-2">
                <Label>Item Name</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g. Rice"
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Quantity</Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.1"
                    value={formData.quantity}
                    onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Unit</Label>
                  <Select
                    value={formData.unit}
                    onValueChange={(val) => setFormData({ ...formData, unit: val })}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {UNITS.map((u) => (
                        <SelectItem key={u} value={u}>
                          {u}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <p className="text-sm text-slate-500">
                This item will be added to <strong>{selectedTerm} {selectedYear}</strong> and must be brought by every child.
              </p>
              <div className="flex justify-end gap-3 pt-2">
                <Button type="button" variant="outline" onClick={() => setIsModalOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-pink-500 hover:bg-pink-600 text-white">
                  {editingItem ? "Update" : "Add Item"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  if (user?.role === "student") {
    const studentItems = items;
    const studentBroughtCount = studentItems.filter((item) =>
      loggedInStudent ? isItemBroughtForStudent(loggedInStudent.id, item.id, allSubmissions) : false
    ).length;

    return (
      <div>
        <div className="mb-8">
          <h1 className="font-heading font-bold text-3xl text-slate-900 flex items-center gap-3">
            <ShoppingBasket className="text-pink-500" size={32} />
            My Groceries
          </h1>
          <p className="text-slate-500 mt-1">View your grocery checklist</p>
        </div>

        <div className="flex flex-wrap gap-4 mb-6">
          <div className="w-40">
            <Label className="text-sm mb-1 block">Current Term</Label>
            <Input value={selectedTerm} readOnly />
          </div>
          <div className="w-28">
            <Label className="text-sm mb-1 block">Year</Label>
            <Input value={selectedYear} readOnly />
          </div>
        </div>

        {!loggedInStudent ? (
          <Card className="border-slate-200">
            <CardContent className="py-12">
              <div className="text-center text-slate-500">
                <ShoppingBasket size={48} className="mx-auto mb-4 opacity-50" />
                <p>Could not find your student record.</p>
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card className="border-slate-200">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="font-heading text-lg">
                  {loggedInStudent.first_name} {loggedInStudent.last_name} — Grocery Checklist
                </CardTitle>
                <Badge
                  className={
                    studentBroughtCount === studentItems.length && studentItems.length > 0
                      ? "bg-green-100 text-green-700"
                      : "bg-slate-100 text-slate-600"
                  }
                >
                  {studentBroughtCount}/{studentItems.length} brought
                </Badge>
              </div>
            </CardHeader>

            <CardContent>
              {loading ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pink-500"></div>
                </div>
              ) : studentItems.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <ShoppingBasket size={48} className="mx-auto mb-4 opacity-50" />
                  <p>No grocery items set for this term yet.</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {studentItems.map((item) => {
                    const brought = isItemBroughtForStudent(loggedInStudent.id, item.id, allSubmissions);

                    return (
                      <div
                        key={item.id}
                        className={`flex items-center gap-4 p-3 rounded-lg border ${
                          brought
                            ? "bg-green-50 border-green-200"
                            : "bg-white border-slate-200"
                        }`}
                      >
                        <div
                          className={`w-6 h-6 rounded-md border-2 flex items-center justify-center flex-shrink-0 ${
                            brought ? "bg-green-500 border-green-500 text-white" : "border-slate-300"
                          }`}
                        >
                          {brought && <Check size={14} />}
                        </div>

                        <div className="flex-1">
                          <span
                            className={`font-medium ${
                              brought ? "text-green-800 line-through" : "text-slate-900"
                            }`}
                          >
                            {item.name}
                          </span>
                          <span className="text-sm text-slate-500 ml-2">
                            {item.quantity} {item.unit}
                          </span>
                        </div>

                        {brought ? (
                          <CheckCircle2 size={20} className="text-green-500 flex-shrink-0" />
                        ) : (
                          <X size={20} className="text-slate-400 flex-shrink-0" />
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    );
  }

  return (
    <div>
      <div className="mb-8">
        <h1 className="font-heading font-bold text-3xl text-slate-900 flex items-center gap-3">
          <ShoppingBasket className="text-pink-500" size={32} />
          Grocery Tracking
        </h1>
        <p className="text-slate-500 mt-1">Record groceries brought by each student</p>
      </div>

      <div className="flex flex-wrap gap-4 mb-6">
        <div className="w-40">
          <Label className="text-sm mb-1 block">Term</Label>
          <Select value={selectedTerm} onValueChange={setSelectedTerm}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {TERMS.map((t) => (
                <SelectItem key={t} value={t}>
                  {t}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="w-28">
          <Label className="text-sm mb-1 block">Year</Label>
          <Input type="number" value={selectedYear} onChange={(e) => setSelectedYear(e.target.value)} />
        </div>

        <div className="flex-1 min-w-[200px]">
          <Label className="text-sm mb-1 block">Select Student</Label>
          <Input
            type="text"
            placeholder="Type to filter by name or ID..."
            value={studentSelectSearch}
            onChange={(e) => setStudentSelectSearch(e.target.value)}
            className="mb-2"
          />
          <Select value={selectedStudent} onValueChange={setSelectedStudent}>
            <SelectTrigger><SelectValue placeholder="Choose a student" /></SelectTrigger>
            <SelectContent>
              {filteredTeacherStudents.map((s) => (
                <SelectItem key={s.id} value={String(s.id)}>
                  {s.student_id_code} — {s.first_name} {s.last_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {selectedStudent ? (
        <Card className="border-slate-200">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="font-heading text-lg">
                {selectedStudentObj?.first_name} {selectedStudentObj?.last_name} — Grocery Checklist
              </CardTitle>
              <Badge
                className={
                  broughtCount === items.length && items.length > 0
                    ? "bg-green-100 text-green-700"
                    : "bg-slate-100 text-slate-600"
                }
              >
                {broughtCount}/{items.length} brought
              </Badge>
            </div>
          </CardHeader>

          <CardContent>
            {loading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pink-500"></div>
              </div>
            ) : items.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <ShoppingBasket size={48} className="mx-auto mb-4 opacity-50" />
                <p>No grocery items set for this term yet.</p>
              </div>
            ) : (
              <div className="space-y-2">
                {items.map((item) => {
                  const brought = isItemBrought(item.id);
                  const isSaving = savingItem === item.id;

                  return (
                    <div
                      key={item.id}
                      onClick={() => !isSaving && handleToggle(item.id)}
                      className={`flex items-center gap-4 p-3 rounded-lg border cursor-pointer transition-all ${
                        brought
                          ? "bg-green-50 border-green-200 hover:bg-green-100"
                          : "bg-white border-slate-200 hover:bg-slate-50"
                      } ${isSaving ? "opacity-60 pointer-events-none" : ""}`}
                    >
                      <div
                        className={`w-6 h-6 rounded-md border-2 flex items-center justify-center flex-shrink-0 ${
                          brought ? "bg-green-500 border-green-500 text-white" : "border-slate-300"
                        }`}
                      >
                        {brought && <Check size={14} />}
                      </div>

                      <div className="flex-1">
                        <span
                          className={`font-medium ${
                            brought ? "text-green-800 line-through" : "text-slate-900"
                          }`}
                        >
                          {item.name}
                        </span>
                        <span className="text-sm text-slate-500 ml-2">
                          {item.quantity} {item.unit}
                        </span>
                      </div>

                      {brought && <CheckCircle2 size={20} className="text-green-500 flex-shrink-0" />}
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      ) : (
        <Card className="border-slate-200">
          <CardContent className="py-12">
            <div className="text-center text-slate-500">
              <ShoppingBasket size={48} className="mx-auto mb-4 opacity-50" />
              <p>Select a student to start recording groceries</p>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}